import { FC } from 'react';
import { useContextualTour } from '@/hooks/useContextualTour';
import { useIsMobile } from '@/hooks/useIsMobile';
import { TUTORIAL_KEYS } from '@/constants/tutorials';
import DesktopTour from './DesktopTour';
import MobileWelcomeModal from './MobileWelcomeModal';
import type { Config } from 'driver.js';

/**
 * Tour contextual de Ranking — dispara na 1ª visita a `/ranking`.
 * Desktop: 4 passos com spotlights. Mobile: modal único de boas-vindas.
 */
const DESKTOP_STEPS: NonNullable<Config['steps']> = [
  {
    popover: {
      title: 'Veja como você e seu setor estão indo',
      description:
        'O ranking mostra quem mais aprendeu na temporada. Bônus e prêmios reais podem rolar com base nele.',
    },
  },
  {
    element: '[data-tour="ranking-toggle"]',
    popover: {
      title: 'Duas visões',
      description:
        '<b>Por Setor</b> mostra a competição entre departamentos. <b>Ranking Geral</b> lista todos os usuários por XP total. Use o toggle pra trocar.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="ranking-podium"]',
    popover: {
      title: 'Os três da cabeça',
      description:
        'Sempre o top 3 destacado, com o líder no centro. Posição mais alta = mais XP nesta temporada.',
      side: 'bottom',
    },
  },
  {
    element: '[data-tour="ranking-table"]',
    popover: {
      title: 'A tabela completa',
      description:
        'Cada linha mostra membros, tendência (subiu/desceu) e XP total. Clica em <b>Ver ranking completo</b> pra ver todos.',
      side: 'top',
    },
  },
];

const RankingTour: FC = () => {
  const { shouldStart, finishTour, skipTour } = useContextualTour(TUTORIAL_KEYS.RANKING, '/ranking');
  const isMobile = useIsMobile();

  if (!shouldStart) return null;

  return isMobile ? (
    <MobileWelcomeModal
      onComplete={finishTour}
      onSkip={skipTour}
      content={{
        emoji: '🏆',
        title: 'Bem-vindo ao Ranking',
        subtitle: 'Veja como você e seu setor estão indo na temporada.',
        tipTitle: 'Como ler:',
        tipBody: (
          <>
            Use o toggle <b className="text-accent">Por Setor / Ranking Geral</b> pra trocar entre
            competição de times e individual. Bônus reais podem sair com base no ranking.
          </>
        ),
        ctaLabel: 'Ver ranking',
      }}
    />
  ) : (
    <DesktopTour steps={DESKTOP_STEPS} onComplete={finishTour} onSkip={skipTour} />
  );
};

export default RankingTour;
